import React, { useEffect, useMemo, useRef, useState } from 'react';

function ChevronDownIcon({ className }) {
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true" fill="currentColor">
      <path d="M5.25 7.5l4.75 4.75 4.75-4.75" />
    </svg>
  );
}

function CheckIcon({ className }) {
  return (
    <svg viewBox="0 0 20 20" className={className} aria-hidden="true" fill="currentColor">
      <path d="M7.75 13.25L4.5 10l1.4-1.4 1.85 1.85 6.35-6.35 1.4 1.4-7.75 7.75z" />
    </svg>
  );
}

function FallbackIcon({ className }) {
  return <div className={`h-4 w-4 rounded-full bg-slate-400 ${className || ''}`} />;
}

export default function PlatformSelectDropdown({ value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const wrapperRef = useRef(null);

  const selectedIndex = useMemo(
    () => options.findIndex((option) => option.key === value),
    [options, value]
  );

  useEffect(() => {
    if (!open) return;
    const nextIndex = selectedIndex >= 0 ? selectedIndex : 0;
    setActiveIndex(nextIndex);
    requestAnimationFrame(() => {
      listRef.current?.focus();
    });
  }, [open, selectedIndex]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleTriggerKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
    }
  };

  const handleListKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, options.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(options.length - 1);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const option = options[activeIndex];
      if (option) {
        onChange(option.key);
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
  };

  const selectedOption = options[selectedIndex] || options[0];

  return (
    <div ref={wrapperRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className="flex items-center gap-2">
          {selectedOption?.icon || <FallbackIcon />}
          <span className="font-medium text-slate-800">
            {selectedOption?.label || 'Select platform'}
          </span>
        </span>
        <ChevronDownIcon className="h-4 w-4 text-slate-500" />
      </button>

      {open && (
        <div
          ref={listRef}
          role="listbox"
          tabIndex={-1}
          aria-activedescendant={`platform-option-${activeIndex}`}
          className="absolute z-20 mt-2 w-full rounded-lg border border-slate-200 bg-white p-1 shadow-lg focus:outline-none"
          onKeyDown={handleListKeyDown}
        >
          {options.map((option, index) => {
            const isSelected = option.key === value;
            const isActive = index === activeIndex;
            return (
              <button
                key={option.key}
                id={`platform-option-${index}`}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${
                  isActive ? 'bg-slate-100' : 'bg-white'
                } hover:bg-slate-100`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => {
                  onChange(option.key);
                  setOpen(false);
                  buttonRef.current?.focus();
                }}
              >
                <span className="flex items-center gap-2 text-slate-800">
                  {option.icon || <FallbackIcon />}
                  {option.label}
                </span>
                {isSelected && <CheckIcon className="h-4 w-4 text-slate-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
